import {
  Box,
  Button,
  Card,
  CardContent,
  Grid2,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import SpeedIcon from "@mui/icons-material/Speed";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DiscordIcon from "../../shared/components/DiscordIcon";
import { useAuth } from "../../shared/auth/AuthContext";
import { brand, isPrettyChitty, isReallyChitty } from "../../shared/util/branding";
import { InternalLink } from "../../shared/components/InternalLink";

const discordInviteUrl = import.meta.env.VITE_DISCORD_INVITE_URL;

export default function Home() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          textAlign: "center",
          py: 6,
          mb: 4,
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            textShadow: `3px 3px 0px ${theme.palette.secondary.main}`,
          }}
        >
          {brand.name}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            mb: 4,
            maxWidth: 500,
            mx: "auto",
          }}
        >
          {brand.description}
        </Typography>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          flexWrap="wrap"
          useFlexGap
        >
          {user && (
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate("/match")}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                px: 4,
                py: 1.5,
                fontWeight: 600,
              }}
              startIcon={<PlayArrowIcon />}
            >
              My Matches
            </Button>
          )}
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/game")}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              px: 4,
              py: 1.5,
              fontWeight: 600,
            }}
          >
            Browse Games
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/features")}
            sx={{
              borderColor: theme.palette.secondary.main,
              color: theme.palette.text.primary,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              "&:hover": {
                borderColor: theme.palette.secondary.light,
                background: "rgba(140, 0, 255, 0.1)",
              },
            }}
          >
            Learn More
          </Button>
        </Stack>
      </Box>

      {/* Info Cards */}
      <Grid2 container spacing={3} sx={{ mb: 4 }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              background: theme.palette.background.paper,
              height: "100%",
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}33 0%, ${theme.palette.primary.main}33 100%)`,
                  }}
                >
                  <SportsEsportsIcon
                    sx={{ color: theme.palette.info.main, fontSize: 28 }}
                  />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {brand.infoCardTitle}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {brand.infoCardText}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card
            sx={{
              background: theme.palette.background.paper,
              height: "100%",
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}33 0%, ${theme.palette.primary.main}33 100%)`,
                  }}
                >
                  <SpeedIcon
                    sx={{ color: theme.palette.info.main, fontSize: 28 }}
                  />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {brand.featureCardTitle}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {brand.featureCardText}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>

      {/* Discord Card */}
      {discordInviteUrl && (
        <Card
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.secondary.main}22 0%, ${theme.palette.primary.main}22 100%)`,
            border: `1px solid ${theme.palette.secondary.main}44`,
            mb: 4,
          }}
        >
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  background: `${theme.palette.secondary.main}33`,
                }}
              >
                <DiscordIcon
                  sx={{ color: theme.palette.secondary.main, fontSize: 28 }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Join Our Discord
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, mb: 2 }}
                >
                  Jump into games with the community, or add the bot to your own
                  server to play with friends.
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    href={discordInviteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      background: "#5865F2",
                      "&:hover": { background: "#4752C4" },
                    }}
                  >
                    Join Discord
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/game")}
                    sx={{
                      borderColor: theme.palette.text.secondary,
                      color: theme.palette.text.primary,
                    }}
                  >
                    Add to Your Server
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Warning Card */}
      <Card
        sx={{
          background: brand.colors.footerBackground,
          border: `1px solid ${theme.palette.primary.main}33`,
          mb: 4,
        }}
      >
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                background: `${theme.palette.primary.main}22`,
              }}
            >
              <WarningAmberIcon
                sx={{ color: theme.palette.primary.main, fontSize: 28 }}
              />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: theme.palette.primary.main,
                }}
              >
                {brand.warningTitle}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {brand.warningMessage}
                {isPrettyChitty() && (
                  <>
                    {" "}
                    <InternalLink to="/contact">Contact us</InternalLink>.
                  </>
                )}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Footer message */}
      {isReallyChitty() && (
        <Box
          sx={{
            textAlign: "center",
            py: 3,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            alignItems="center"
          >
            <FavoriteIcon
              sx={{ color: theme.palette.primary.main, fontSize: 20 }}
            />
            <Typography
              variant="body1"
              sx={{ color: theme.palette.text.secondary }}
            >
              Play with people you trust. Have fun.
            </Typography>
            <FavoriteIcon
              sx={{ color: theme.palette.primary.main, fontSize: 20 }}
            />
          </Stack>
        </Box>
      )}
    </Box>
  );
}
